import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Grid,
  TextField,
  Autocomplete,
  TablePagination
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Plus as PlusIcon, RefreshCw, Calculator as TabellariLogoIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { toast } from 'react-toastify';

export default function FeeConfigs() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipiRapporto, setTipiRapporto] = useState([]);
  const [filters, setFilters] = useState({
    tipo_rapporto_id: '',
    valid_date: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTipi();
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [filters]);

  const fetchTipi = async () => {
    try {
      const response = await api.get('/tipi-rapporto');
      setTipiRapporto(response.data);
    } catch (error) {
      console.error('Error fetching tipi rapporto:', error);
    }
  };

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.tipo_rapporto_id) params.tipo_rapporto_id = filters.tipo_rapporto_id;
      if (filters.valid_date) params.valid_date = filters.valid_date;

      const response = await api.get('/fee-configs', { params });
      setConfigs(response.data);
    } catch (err) {
      console.error('Error fetching fee configs:', err);
      toast.error('Errore nel caricamento dei tabellari');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo tabellario?')) {
      try {
        await api.delete(`/fee-configs/${id}`);
        toast.success('Tabellario eliminato');
        fetchConfigs();
      } catch (err) {
        console.error('Error deleting fee config:', err);
        toast.error('Errore durante l\'eliminazione');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const isActive = (config) => {
    const now = new Date();
    const from = new Date(config.valid_from);
    const to = config.valid_to ? new Date(config.valid_to) : null;
    return now >= from && (!to || now <= to);
  };

  const paginatedConfigs = configs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <TabellariLogoIcon className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-semibold text-gray-800 dark:text-gray-100">
              Tabellari (Fee Schedules)
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Gestisci i tabellari per il calcolo automatico dei compensi
            </Typography>
          </div>
        </div>
        <Button onClick={() => navigate('/fee-configs/new')} className="gap-2">
          <PlusIcon className="h-4 w-4" />
          Nuovo Tabellario
        </Button>
      </div>

      <Card className="p-4 shadow-sm border-none">
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <Autocomplete
              options={tipiRapporto}
              getOptionLabel={(option) => option.label + (option.code ? ` (${option.code})` : '')}
              value={
                filters.tipo_rapporto_id
                  ? tipiRapporto.find(t => t.id === parseInt(filters.tipo_rapporto_id)) || null
                  : null
              }
              onChange={(event, newValue) => {
                setFilters(prev => ({
                  ...prev,
                  tipo_rapporto_id: newValue ? newValue.id : ''
                }));
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Filtra per Tipo Rapporto" 
                  placeholder="Seleziona un tipo..."
                  fullWidth 
                  size="small"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              type="date"
              label="Valido al"
              value={filters.valid_date}
              onChange={(e) => setFilters(prev => ({ ...prev, valid_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setFilters({ tipo_rapporto_id: '', valid_date: '' })}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Card className="overflow-hidden border shadow-sm">
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                <TableCell className="font-semibold">Nome</TableCell>
                <TableCell className="font-semibold">Valido Dal</TableCell>
                <TableCell className="font-semibold">Valido Al</TableCell>
                <TableCell className="font-semibold">Stato</TableCell>
                <TableCell className="font-semibold text-right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" className="py-8">
                    <RefreshCw className="animate-spin h-6 w-6 text-gray-400 mx-auto" />
                  </TableCell>
                </TableRow>
              ) : configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" className="py-8 text-gray-500">
                    Nessun tabellario trovato. Creane uno nuovo.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedConfigs.map((config) => (
                  <TableRow key={config.id} hover>
                    <TableCell>{config.name}</TableCell>
                    <TableCell>{formatDate(config.valid_from)}</TableCell>
                    <TableCell>{formatDate(config.valid_to)}</TableCell>
                    <TableCell>
                      {isActive(config) ? (
                        <Chip label="Attivo" color="success" size="small" variant="outlined" />
                      ) : (
                        <Chip label="Non Attivo" color="default" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <div className="flex justify-end gap-2">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/fee-configs/${config.id}`)}
                          color="primary"
                        >
                          <EditIcon size={18} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(config.id)}
                          color="error"
                        >
                          <DeleteIcon size={18} />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={configs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`}
        />
      </Card>
    </Box>
  );
}
